<template>
	<app-page>
		<app-heading :title="isNew ? 'New Navigation Item' : navItem.name" />
		<app-form @submit="onSubmit">
			<app-row>
				<app-cell cols="12" md="6">
					<app-section class="mt-4">
						<app-section-actions>
							<app-button type="submit" small color="success" v-if="isNew">Create</app-button>
							<app-button type="submit" small color="primary" v-else>Save</app-button>
							<app-button type="cancel" small :to="{ name: 'website-navitems' }">Cancel</app-button>
						</app-section-actions>
						<input-text
							label="*Name"
							v-model="navItem.name"
							required
							:errors="errors.name"
							hint="A meaningful name will allow to easily recognize the navigation purpose"
						/>
						<input-text
							label="*Slug (Display ID)"
							v-model="navItem.slug"
							required
							:disabled="!isNew"
							:errors="errors.slug"
							:hint="`The slug can't be updated once set. `
								+ `This should be the same as the page counterpart if the URL is internal`"
						/>
						<app-row no-padding>
							<app-cell v-for="(ancestor, i) in ancestors" :key="`ancestor-${i}`">
								<input-select
									v-model="ancestors[i]"
									label="Parent(s)"
									:items="getParents(i)"
									required
									clearable
									:errors="errors.parent"
								/>
							</app-cell>
						</app-row>
						<input-text
							label="*Url"
							v-model="navItem.url"
							required
							:errors="errors.url"
							:hint="`URL without the first / and without the parent URL. `
								+ `Start with http(s):// ONLY if it's an external link. Use / to link to the home page`"
						/>
						<input-select
							label="Internal Page"
							:items="pages"
							v-model="navItem.page"
							clearable
							required
							:disabled="navItem.url === '' || navItem.url === '/' || /http(s):\/\//.test(navItem.url)"
							:errors="errors.page"
						/>
						<!-- @TODO Move this section into a input-tags component -->
						<div class="tags-section">
							<input-chipbox
								v-model="navItem.tags[0]"
								:items="tags"
								multiple
								label="Use as root page for units & complexes with the following tag(s)"
							/>
							<app-row no-padding v-for="(tag, i) in navItem.tags.slice(1)" :key="`tags-${i}`">
								<app-cell cols="12" md="2" sm="3" align-self="center">
									<div style="height: 20px;">
										<app-chip
											color="primary"
											clearable
											@click:close="removeListingsAndTags(i+1)"
										>AND</app-chip>
									</div>
								</app-cell>
								<app-cell>
									<input-chipbox
										:items="tags"
										v-model="navItem.tags[i + 1]"
										multiple
										placeholder="Additional Tags"
									/>
								</app-cell>
							</app-row>
							<div class="mt-4">
								<app-button color="success" small icon="fa-plus" @click="addListingsAndTags">AND</app-button>
							</div>
						</div>
						<input-select
							label="Use as root page for units & complexes with the following tag(s)"
							:items="tags"
							v-model="navItem.tags"
							required
							multiple
							:errors="errors.tags"
						/>
						<app-row>
							<app-cell cols="12" md="6">
								<input-switch
									label="Is hidden in main navigation?"
									v-model="navItem.hidden"
									:errors="errors.hidden"
								/>
							</app-cell>
							<app-cell cols="12" md="6">
								<input-switch
									label="Is featured link in footer?"
									v-model="navItem.featured"
									:errors="errors.featured"
								/>
							</app-cell>
						</app-row>
						<div
							v-for="(locale, i) in locales"
							:key="`locale-${i}`"
						>
							<div>{{locale | locale}}</div>
							<input-text
								v-if="locale !== 'en'"
								label="Translated URL (keep empty to use the same url)"
								hint="Write a a new URL only if you intent to translate the original url"
								:placeholder="navItem.url"
								v-model="navItem.url_translations[locale]"
								required
								:errors="errors.url_translations"
							/>
							<input-textarea
								label="Display text"
								placeholder="Type some text...."
								v-model="navItem.text[locale]"
								required
								:errors="errors.text"
							/>
						</div>
					</app-section>
				</app-cell>
			</app-row>
		</app-form>
	</app-page>
</template>

<script>
import { clearEmptyFields } from '@/utils/collection'

export default {
	data() {
		return {
			navItem: {
				slug: '',
				url: '',
				url_translations: {},
				name: '',
				parent: null,
				page: null,
				text: {},
				tags: [],
				featured: false,
				hidden: false,
			},
			ancestors: [null],
			tags: [[]],
			pages: [],
			oriParent: null,
			parentItems: [],
			locales: [],
			errors: {},
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
		id() {
			return this.$route.params.item
		},
		isNew() {
			return this.id === 'new'
		},
	},
	watch: {
		ancestors(value) {
			const nullIndex = value.indexOf(null)
			if (nullIndex === -1) {
				this.ancestors.push(null)
			} else if (nullIndex < this.ancestors.length - 1) {
				const ancestors = [...this.ancestors]
				ancestors.splice(nullIndex, this.ancestors.length - 1 - nullIndex)
				this.ancestors = ancestors
			}

			let parent = this.ancestors[this.ancestors.length - 1]
			if (!parent && this.ancestors.length > 1) {
				parent = this.ancestors[this.ancestors.length - 2]
			}

			if (parent !== this.navItem.parent) {
				this.navItem.parent = parent
			}
		},
	},
	async mounted() {
		this.$store.dispatch('app/startLoading')
		try {
			if (this.id !== 'new') {
				const navItem = await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('navitems')
					.to(this.id)
					.get()

				// Migrate old tags notations to the new format
				if (navItem.tags) {
					if (!navItem.tags.length) {
						navItem.tags = [[]]
					}
					if (typeof navItem.tags[0] === 'string') {
						navItem.tags = navItem.tags.map(tag => [tag])
					}
				}
				this.navItem = navItem
				this.ancestors = this.navItem.ancestors || []
				this.oriParent = this.navItem.parent
			}

			const datasourceRequest = this.$ravello.graphql('navItems')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.datasource('name', '_id', {
					extraFields: ['parent'],
				})
				.sort('position')
				.union(this.$ravello.graphql('tags')
					.parent(this.$ravello.graphql('website', this.websiteId))
					.sort('name')
					.datasource('name', 'slug'))
				.union(this.$ravello.graphql('pages')
					.parent(this.$ravello.graphql('website', this.websiteId))
					.sort('name')
					.filter('active', true)
					.filter('or', [{ fixed: { exists: false } }, { fixed: false }])
					.datasource())

			if (this.id !== 'new') {
				datasourceRequest.filter('_id', { ne: this.id })
			}

			const [website, datasources] = await Promise.all([
				this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.field('locales')
					.get(),
				datasourceRequest.query(true),
			])

			this.locales = website.locales || []
			this.parentItems = datasources.navItems
			this.tags = datasources.tags
			this.pages = datasources.pages
			this.locales.forEach((locale) => {
				if (!this.navItem.text[locale]) {
					this.navItem.text[locale] = ''
				}
				if (!this.navItem.url_translations) {
					this.navItem.url_translations = {}
				}
				if (locale !== 'en' && !this.navItem.url_translations[locale]) {
					this.navItem.url_translations[locale] = ''
				}
			})
		} catch (err) {
			this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
		}
		this.$store.dispatch('app/stopLoading')
	},
	methods: {
		getParents(position) {
			if (position === 0) {
				return this.parentItems.filter(item => !item.parent && item._id !== this.navItem._id)
			}
			return this.parentItems.filter(item => item.parent === this.ancestors[position - 1] && item._id !== this.navItem._id)
		},

		addListingsAndTags() {
			this.navItem.tags = [...this.navItem.tags, []]
		},

		removeListingsAndTags(index) {
			const softClone = [...this.navItem.tags]
			softClone.splice(index, 1)
			this.navItem.tags = softClone
		},

		async onSubmit() {
			this.$store.dispatch('app/startLoading')
			try {
				const request = this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('navitems')

				const payload = {
					slug: this.navItem.slug,
					url: this.navItem.url,
					url_translations: clearEmptyFields(this.navItem.url_translations),
					name: this.navItem.name,
					text: this.navItem.text,
					hidden: this.navItem.hidden,
					featured: this.navItem.featured,
					tags: this.navItem.tags || [],
					page: this.navItem.page || null,
					...(this.navItem.parent !== this.oriParent ? { parent: this.navItem.parent } : {}),
				}

				if (this.isNew) {
					await request
						.create(payload)
				} else {
					await request
						.to(this.id)
						.update(payload)
				}

				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', `Navigation item successfully ${this.isNew ? 'added' : 'updated'}`)
				this.$router.push({ name: 'website-navitems' })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},
	},
}
</script>

<style scoped>
	.tags-section {
		padding: 20px;
		margin-top: 10px;
		border: 1px solid #f1f1f1;
	}
</style>
